import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { movimientosEmpleadosAPI, empleadosAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import '../Clientes.css';
import '../Movimientos.css';
import { loadFiltrosSession, saveFiltrosSession } from '../utils/filtrosSession';

const getSortValue = (mov, colId) => {
  switch (colId) {
    case 'fecha':
      return new Date(mov.fecha || 0).getTime() || 0;
    case 'concepto':
      return (mov.concepto || '').toString().toLowerCase();
    case 'debito':
      return mov.tipo_movimiento === 'DEBITO' ? (parseFloat(mov.monto) || 0) : 0;
    case 'credito':
      return mov.tipo_movimiento === 'CREDITO' ? (parseFloat(mov.monto) || 0) : 0;
    case 'saldo':
      return parseFloat(mov.saldo_acumulado || 0) || 0;
    default:
      return '';
  }
};

const deltaSaldo = (mov) => {
  const m = parseFloat(mov.monto) || 0;
  return mov.tipo_movimiento === 'DEBITO' ? m : -m;
};

const EmpleadoMovimientos = () => {
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const [empleado, setEmpleado] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const primeraCargaMovRef = useRef(true);
  const [refrescando, setRefrescando] = useState(false);
  const cargaMovSeqRef = useRef(0);
  const [filtros, setFiltros] = useState(() =>
    loadFiltrosSession(
      id ? `empleado_${id}_movimientos_filtros` : 'empleado_movimientos_filtros',
      { fecha_desde: '', fecha_hasta: '' }
    )
  );
  const fechaDefaultAplicadaRef = useRef(false);
  const [sortByColumn, setSortByColumn] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [saldoFiltrado, setSaldoFiltrado] = useState(null);
  const [saldoCuenta, setSaldoCuenta] = useState(null);
  const [saldoInicial, setSaldoInicial] = useState(null);
  const { formatFecha, fechaHoy } = useZonaHoraria();

  useEffect(() => {
    if (!id) return;
    setFiltros(
      loadFiltrosSession(`empleado_${id}_movimientos_filtros`, { fecha_desde: '', fecha_hasta: '' })
    );
    fechaDefaultAplicadaRef.current = false;
    primeraCargaMovRef.current = true;
  }, [id]);

  useEffect(() => {
    if (!id) return;
    saveFiltrosSession(`empleado_${id}_movimientos_filtros`, filtros);
  }, [id, filtros]);

  useEffect(() => {
    if (fechaDefaultAplicadaRef.current) return;
    fechaDefaultAplicadaRef.current = true;
    setFiltros((prev) => (prev.fecha_desde ? prev : { ...prev, fecha_desde: fechaHoy() }));
  }, [fechaHoy]);

  const loadEmpleado = useCallback(async () => {
    if (!id) return;
    try {
      const response = await empleadosAPI.getById(id);
      if (response.success) setEmpleado(response.data);
    } catch {
      toast.error('Error al cargar empleado');
    }
  }, [id]);

  const loadMovimientos = useCallback(async () => {
    if (!id) return;
    const mostrarLoaderPantalla = primeraCargaMovRef.current;
    const seq = ++cargaMovSeqRef.current;
    try {
      if (mostrarLoaderPantalla) setLoading(true);
      else setRefrescando(true);
      const params = {};
      if (filtros.fecha_desde) {
        params.fecha_desde = filtros.fecha_desde;
        params.incluir_saldo_inicial = '1';
      }
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      const response = await movimientosEmpleadosAPI.getByEmpleado(id, params);
      if (seq !== cargaMovSeqRef.current) return;
      if (response.success) {
        setMovimientos(Array.isArray(response.data) ? response.data : []);
        setSaldoFiltrado(response.saldo_filtrado !== undefined ? response.saldo_filtrado : null);
        setSaldoCuenta(response.saldo_cuenta !== undefined ? response.saldo_cuenta : null);
        setSaldoInicial(response.saldo_inicial !== undefined ? response.saldo_inicial : null);
      } else {
        setMovimientos([]);
        setSaldoFiltrado(null);
        setSaldoCuenta(null);
        setSaldoInicial(null);
      }
    } catch (error) {
      if (seq !== cargaMovSeqRef.current) return;
      setMovimientos([]);
      setSaldoFiltrado(null);
      setSaldoCuenta(null);
      setSaldoInicial(null);
      toast.error(error.message || 'Error al cargar movimientos');
    } finally {
      if (mostrarLoaderPantalla) {
        setLoading(false);
        primeraCargaMovRef.current = false;
      }
      if (seq === cargaMovSeqRef.current) setRefrescando(false);
    }
  }, [id, filtros.fecha_desde, filtros.fecha_hasta]);

  useEffect(() => { loadEmpleado(); }, [loadEmpleado]);
  useEffect(() => { loadMovimientos(); }, [loadMovimientos]);

  const formatMoneda = (n) =>
    parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const hayFiltrosActivos = !!(filtros.fecha_desde || filtros.fecha_hasta);

  const saldoCuentaReal = useMemo(() => {
    if (saldoCuenta !== null) return saldoCuenta;
    return movimientos.reduce((acc, m) => acc + deltaSaldo(m), 0);
  }, [movimientos, saldoCuenta]);

  const movimientosOrdenados = useMemo(() => {
    if (!sortByColumn) return [...movimientos];
    const arr = [...movimientos];
    arr.sort((a, b) => {
      const v1 = getSortValue(a, sortByColumn);
      const v2 = getSortValue(b, sortByColumn);
      const num = typeof v1 === 'number' && typeof v2 === 'number';
      let cmp = 0;
      if (num) cmp = v1 - v2;
      else cmp = String(v1).localeCompare(String(v2), 'es');
      return sortAsc ? cmp : -cmp;
    });
    return arr;
  }, [movimientos, sortByColumn, sortAsc]);

  const handleSortColumn = (columnId) => {
    if (sortByColumn === columnId) setSortAsc((prev) => !prev);
    else { setSortByColumn(columnId); setSortAsc(true); }
  };

  return (
    <div className="movimientos pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Movimientos del empleado</h1>
        <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
          <Link to="/empleados" className="btn-secondary">← Volver a empleados</Link>
          {hasPermission('crear_pagos_empleados') && (
            <Link to={`/empleados/${id}/pago`} className="btn-primary">Registrar pago</Link>
          )}
        </div>
      </div>

      <div className="pagina-listado-fijo">
      {empleado && (
        <div style={{
          padding: '14px',
          background: '#f0f4f8',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #ddd',
        }}>
          <strong>{empleado.nombre}</strong>
          {empleado.telefono && <span style={{ marginLeft: '12px', color: '#666' }}>Tel: {empleado.telefono}</span>}
          {empleado.sueldo != null && (
            <span style={{ marginLeft: '12px' }}>Sueldo: ${formatMoneda(empleado.sueldo)}</span>
          )}
          {!hayFiltrosActivos && (
            <span style={{ marginLeft: '12px', fontWeight: 'bold' }}>
              Saldo de la cuenta: ${formatMoneda(saldoCuentaReal)}
            </span>
          )}
        </div>
      )}

      {hayFiltrosActivos && (saldoFiltrado !== null || saldoCuenta !== null || saldoInicial !== null) && (
        <div style={{ marginBottom: 16, padding: 12, background: '#f0f4f8', borderRadius: 8, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {saldoFiltrado !== null && (
            <div>
              <strong>Saldo de los movimientos filtrados</strong>:{' '}
              <span className={saldoFiltrado > 0 ? 'negative' : saldoFiltrado < 0 ? 'positive' : ''}>
                ${formatMoneda(saldoFiltrado)}
              </span>
            </div>
          )}
          {saldoCuenta !== null && (
            <div>
              <strong>Saldo acumulado de la cuenta (sin filtros)</strong>:{' '}
              <span className={saldoCuenta > 0 ? 'negative' : saldoCuenta < 0 ? 'positive' : ''}>
                ${formatMoneda(saldoCuenta)}
              </span>
            </div>
          )}
          {saldoInicial !== null && (
            <div>
              <strong>Saldo inicial</strong> {filtros.fecha_desde && `(al ${formatFecha(filtros.fecha_desde)}): `}
              <span className={saldoInicial > 0 ? 'negative' : saldoInicial < 0 ? 'positive' : ''}>
                ${formatMoneda(saldoInicial)}
              </span>
            </div>
          )}
        </div>
      )}
      </div>

      <div className="listado-panel clientes-listado listado-controles">
        <div className="filters" style={{ flexWrap: 'wrap', gap: '8px 12px', alignItems: 'center' }}>
          <input
            type="date"
            name="fecha_desde"
            value={filtros.fecha_desde}
            onChange={(e) => setFiltros((prev) => ({ ...prev, fecha_desde: e.target.value }))}
            placeholder="Fecha desde"
          />
          <input
            type="date"
            name="fecha_hasta"
            value={filtros.fecha_hasta}
            onChange={(e) => setFiltros((prev) => ({ ...prev, fecha_hasta: e.target.value }))}
            placeholder="Fecha hasta"
          />
        </div>

      <div className="listado-panel-cuerpo clientes-listado-cuerpo">
      <div className={`listado-tabla-area clientes-listado-tabla-area${refrescando ? ' listado-tabla-area--refreshing clientes-listado-tabla-area--refreshing' : ''}`}>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {[
                { id: 'fecha', label: 'Fecha' },
                { id: 'concepto', label: 'Concepto' },
                { id: 'debito', label: 'Débito' },
                { id: 'credito', label: 'Crédito' },
                { id: 'saldo', label: 'Saldo acumulado' },
              ].map((col) => (
                <th
                  key={col.id}
                  className="th-sortable"
                  onClick={() => handleSortColumn(col.id)}
                  title={`Ordenar por ${col.label}`}
                >
                  <span>{col.label}</span>
                  {sortByColumn === col.id && (
                    <span className="sort-indicator" aria-hidden="true">{sortAsc ? ' ▲' : ' ▼'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="no-data">Cargando...</td></tr>
            ) : movimientosOrdenados.length === 0 ? (
              <tr><td colSpan="5" className="no-data">No hay movimientos en el período</td></tr>
            ) : (
              movimientosOrdenados.map((mov) => (
                <tr key={mov.id}>
                  <td>{formatFecha(mov.fecha)}</td>
                  <td>{mov.concepto}</td>
                  <td className="negative">
                    {mov.tipo_movimiento === 'DEBITO' ? <strong>${formatMoneda(mov.monto)}</strong> : '-'}
                  </td>
                  <td className="positive">
                    {mov.tipo_movimiento === 'CREDITO' ? <strong>${formatMoneda(mov.monto)}</strong> : '-'}
                  </td>
                  <td
                    className={
                      parseFloat(mov.saldo_acumulado || 0) > 0
                        ? 'negative'
                        : parseFloat(mov.saldo_acumulado || 0) < 0
                          ? 'positive'
                          : ''
                    }
                  >
                    <strong>${formatMoneda(mov.saldo_acumulado)}</strong>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {refrescando && (
        <div className="listado-refresh-overlay clientes-listado-refresh-overlay" role="status" aria-live="polite">
          <span className="clientes-listado-refresh-spinner" aria-hidden="true" />
          <span className="clientes-listado-refresh-label">Actualizando…</span>
        </div>
      )}
      </div>
      </div>
      </div>
    </div>
  );
};

export default EmpleadoMovimientos;
